// El aviso de Santi DENTRO de la oficina abierta (04-11, Pau: «no me llega ninguna notificación. Yo quiero poder elegir
// el sonido y tal»): que la oficina avise por sí misma, sin depender de que el sistema (Windows) enseñe la suya.
// Cuando llega un `notification:new` de Santi (su fila lleva `etiqueta` santi-<hilo> o `url` ?santi=<hilo>; el servidor
// solo se la manda a las conexiones de Pau), abajo a la derecha sale «Santi» y lo que escribió (una línea) y suena el
// sonido elegido (sonidos.ts, «Avisos y sonido»). Pulsarlo abre esa conversación en la ventana «Santi». Se va solo a
// los 8 s (con el ratón encima, espera); otro del mismo hilo lo sustituye (no se apilan: como mucho uno por hilo y tres
// a la vez). No sale (ni suena) si Pau ya tiene ESE hilo abierto y lo está mirando (pestaña a la vista, con el foco y
// sin estar parado: usePauMira). Con la pestaña de fondo sale igual y suena si el navegador deja (tras un clic o una
// tecla en la página: lib/sonido.ts `desbloquearAudioAlPrimerGesto`).
// Solo la oficina (OficinaPage): en /home ya está el toast de la campana (useSocket).
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { desbloquearAudioAlPrimerGesto } from '../../../lib/sonido';
import { getSocket } from '../../../lib/socket';
import { cn } from '../../../lib/utils';
import type { FeedNotification } from '../../../stores/notifications.store';
import { usePauMira } from '../pelotas/mirando';
import { AvatarPersona } from '../pelotas/piezas';
import { useHablaComoPau, usePelotasStore } from '../pelotas/store';
import { BotonIcono } from '../ui';
import { tocarSonido } from './sonidos';

export const DURACION_AVISO_MS = 8_000;
export const MAX_AVISOS = 3;

export interface AvisoEnOficina {
  etiqueta: string;
  hilo: number | 'general';
  titulo: string;
  texto: string;
}

/** El hilo de Santi de una fila de la campana (por su `url` ?santi=… o su `etiqueta` santi-…), o null si no es de Santi. */
export function hiloDeAviso(n: Pick<FeedNotification, 'url' | 'etiqueta'>): number | 'general' | null {
  const deUrl = (() => {
    if (!n.url) return null;
    try {
      return new URL(n.url, 'https://oficina.invalid/').searchParams.get('santi');
    } catch {
      return null;
    }
  })();
  const deEtiqueta = n.etiqueta?.startsWith('santi-') ? n.etiqueta.slice('santi-'.length) : null;
  const v = deUrl ?? deEtiqueta;
  if (v === 'general') return 'general';
  return v && /^\d+$/.test(v) ? Number(v) : null;
}

/** Lo que enseña el aviso, o null si la fila no es de Santi. */
export function avisoDe(n: FeedNotification): AvisoEnOficina | null {
  const hilo = hiloDeAviso(n);
  if (hilo === null) return null;
  return { etiqueta: n.etiqueta ?? `santi-${hilo}`, hilo, titulo: n.title, texto: n.body.replace(/\s+/g, ' ').trim() };
}

/** Los avisos a la vista: el nuevo abajo (junto a la esquina); el del mismo hilo se sustituye; como mucho MAX_AVISOS. */
export function ponerAviso(avisos: AvisoEnOficina[], nuevo: AvisoEnOficina): AvisoEnOficina[] {
  return [...avisos.filter((a) => a.etiqueta !== nuevo.etiqueta), nuevo].slice(-MAX_AVISOS);
}

export function AvisosDeSanti({ alto = false }: { alto?: boolean }) {
  const comoPau = useHablaComoPau();
  const mirando = usePauMira();
  // Leído en el momento del aviso (el oyente del socket no se vuelve a montar por esto).
  const mirandoAhora = useRef(mirando);
  useEffect(() => {
    mirandoAhora.current = mirando;
  }, [mirando]);
  const [avisos, setAvisos] = useState<AvisoEnOficina[]>([]);
  const relojes = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const quitar = useCallback((etiqueta: string) => {
    const r = relojes.current.get(etiqueta);
    if (r) clearTimeout(r);
    relojes.current.delete(etiqueta);
    setAvisos((as) => as.filter((a) => a.etiqueta !== etiqueta));
  }, []);
  const programar = useCallback(
    (etiqueta: string) => {
      const r = relojes.current.get(etiqueta);
      if (r) clearTimeout(r);
      relojes.current.set(
        etiqueta,
        setTimeout(() => quitar(etiqueta), DURACION_AVISO_MS),
      );
    },
    [quitar],
  );

  useEffect(() => {
    const dejar = desbloquearAudioAlPrimerGesto();
    return dejar;
  }, []);

  useEffect(() => {
    if (!comoPau) return;
    const socket = getSocket();
    const alAviso = (e: { notification?: FeedNotification }) => {
      const aviso = e?.notification ? avisoDe(e.notification) : null;
      if (!aviso) return;
      // Ya lo está viendo: la conversación lo enseña.
      if (usePelotasStore.getState().abierto === aviso.hilo && mirandoAhora.current) return;
      setAvisos((as) => ponerAviso(as, aviso));
      programar(aviso.etiqueta);
      void tocarSonido();
    };
    socket.on('notification:new', alAviso);
    return () => {
      socket.off('notification:new', alAviso);
    };
  }, [comoPau, programar]);

  useEffect(() => {
    const r = relojes.current;
    return () => {
      for (const id of r.values()) clearTimeout(id);
      r.clear();
    };
  }, []);

  return (
    <TarjetasAvisos
      avisos={avisos}
      alto={alto}
      alAbrir={(a) => {
        quitar(a.etiqueta);
        usePelotasStore.getState().abrirSanti(a.hilo);
      }}
      alQuitar={quitar}
      alEsperar={(etiqueta) => {
        const r = relojes.current.get(etiqueta);
        if (r) clearTimeout(r);
      }}
      alSeguir={programar}
    />
  );
}

/**
 * Los avisos, abajo a la derecha (por encima de la caja de escribir si hay una ventana abierta: `alto`). Un panel
 * flotante de la oficina (D-037): gris de ventana, filo blanco tenue, sombra de hoja; entra en 300 ms con la curva de
 * Apple. Sin estado propio (lo pinta AvisosDeSanti y, fijo, la historia de Storybook).
 */
export function TarjetasAvisos({
  avisos,
  alto = false,
  fijo = true,
  alAbrir,
  alQuitar,
  alEsperar,
  alSeguir,
}: {
  avisos: AvisoEnOficina[];
  alto?: boolean;
  /** false: en su sitio (la historia de Storybook), no flotando. */
  fijo?: boolean;
  alAbrir: (a: AvisoEnOficina) => void;
  alQuitar: (etiqueta: string) => void;
  alEsperar?: (etiqueta: string) => void;
  alSeguir?: (etiqueta: string) => void;
}) {
  if (!avisos.length) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2',
        fijo && 'pointer-events-none fixed right-4 z-[66]',
        fijo && (alto ? 'bottom-[calc(9rem+env(safe-area-inset-bottom))]' : 'bottom-[calc(1rem+env(safe-area-inset-bottom))]'),
      )}
      data-testid="avisos-santi"
    >
      {avisos.map((a) => (
        <div
          key={a.etiqueta}
          className="pointer-events-auto flex items-center gap-1 rounded-o-l bg-oficina-elevada py-1.5 pl-1.5 pr-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 ease-o-apple"
          onPointerEnter={() => alEsperar?.(a.etiqueta)}
          onPointerLeave={() => alSeguir?.(a.etiqueta)}
          data-testid="aviso-santi"
          data-etiqueta={a.etiqueta}
        >
          <button
            type="button"
            onClick={() => alAbrir(a)}
            aria-label={t('oficinaV2.avisosAparato.aviso.abrir', { titulo: a.titulo, texto: a.texto })}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-o-m px-2 py-1.5 text-left outline-none transition-colors duration-150 ease-o-apple hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
            data-testid="aviso-santi-abrir"
          >
            <AvatarPersona persona="santi" tamano={32} />
            <span className="min-w-0 flex-1">
              <span className="block text-o-texto font-semibold text-oficina-texto">{a.titulo}</span>
              <span className="block truncate text-o-sub text-oficina-texto2" data-testid="aviso-santi-texto">
                {a.texto}
              </span>
            </span>
          </button>
          <BotonIcono icono={X} tamano="sm" etiqueta={t('oficinaV2.avisosAparato.quitar')} onClick={() => alQuitar(a.etiqueta)} data-testid="aviso-santi-quitar" />
        </div>
      ))}
    </div>
  );
}
